import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ModalNotificationService } from '../notification/modal-notification.service';
import { ModalDialogNotificationComponent } from '../modal-dialog/modal-dialog-notification.component';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private injector = inject(Injector);

  public static readonly ERROR_DIALOG_TITLE = 'Unfortunately, an unexpected error happened.';
  private readonly ERROR_DIALOG_DESCRIPTION = `<p><br>  We\'re sorry for the hassle. Please reload this page to continue. <br>If this problem happens repeatedly, please report it to us. <br> The details can be found in the console.</p>`;
  private readonly ERRORED_NOTIFICATION_ALERT_MESSAGE = `We're sorry for the hassle. Please reload this page to continue. \nIf this problem happens repeatedly, please report it to us. \n The details can be found in the console`;


  private matDialog: MatDialog;
  private modalNotificationService: ModalNotificationService;

  constructor() {
    setTimeout(() => {
      try {
        this.matDialog = this.injector.get(MatDialog);
        this.modalNotificationService = this.injector.get(ModalNotificationService);
      } catch (_) {
        // Injector may be destroyed before this callback fires (e.g. during test teardown)
      }
    });
  }

  logError(error: any): void {
    console.error('>>> GlobalErrorHandler: unhandled error', new Date().toISOString());
    console.error(error.stack);
    console.error('%O', error);
    console.error('<<<');
  }

  private isCancellationLikeError(error: any): boolean {
    if (!error) {
      return false;
    }

    const message = `${error?.message ?? error}`.toLowerCase();
    const name = `${error?.name ?? ''}`.toLowerCase();

    const containsCanceledText = message.includes('canceled') || name.includes('canceled');

    return containsCanceledText;
  }

  /**
   * Monaco editor has a known incompatibility with Zone.js where its context menu `List.setElements`
   * receives a Zone-wrapped non-array, causing a TypeError. This error is benign — the context menu
   * either recovers or the user sees a brief visual glitch but no data loss.
   * See: https://github.com/microsoft/monaco-editor/issues/2680
   */
  private isMonacoInternalError(error: any): boolean {
    if (!error) {
      return false;
    }
    const stack = `${error?.stack ?? ''}`;
    const message = `${error?.message ?? ''}`;
    const hasKnownMonacoFrame = stack.includes('List.setElements') || stack.includes('setElements');
    const hasKnownReduceError = /\breduce is not a function\b/i.test(message);

    return stack.includes('editor.main.js') && hasKnownMonacoFrame && hasKnownReduceError;
  }

  isSameErrorDialogOpened(): boolean {
    return this.matDialog && this.matDialog.openDialogs
      .filter((dialog: MatDialogRef<any>) => dialog.componentInstance instanceof ModalDialogNotificationComponent)
      .some((dialog: MatDialogRef<ModalDialogNotificationComponent>) => {
        return dialog.componentInstance.data.notificationMessage.title === GlobalErrorHandler.ERROR_DIALOG_TITLE;
      });
  }

  handleError(error: any): void {
    if (this.isCancellationLikeError(error)) {
      return;
    }

    if (this.isMonacoInternalError(error)) {
      this.logError(error);
      return;
    }

    this.logError(error);
    try {
      if (!this.isSameErrorDialogOpened()) {
        this.modalNotificationService.openErrorDialog({
          title: GlobalErrorHandler.ERROR_DIALOG_TITLE,
          description: this.ERROR_DIALOG_DESCRIPTION
        });
      }
    } catch (notificationError) {
      this.logError(notificationError);
      alert(this.ERRORED_NOTIFICATION_ALERT_MESSAGE);
    }
  }
}
